import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Zap, DollarSign, Bell, FileText, Users,
  CheckSquare, TrendingUp, Download, ArrowRight, ChevronRight,
  Activity, MessageCircle, ClipboardList, Check, Sparkles
} from 'lucide-react';

const IMG_V = `?v=${Date.now()}`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const features = [
  { icon: LayoutDashboard, title: 'Pipeline Management', desc: 'Customizable stages that match your sales process. Track every deal from first touch to close.', span: 'md:col-span-4 lg:col-span-8', accent: '#4f46e5', popupImage: '/feature-pipeline.webp' },
  { icon: DollarSign, title: 'Commission Tracking', desc: 'Auto-calculated commissions with quota attainment, multipliers, and spiff bonuses. Know your earnings in real-time.', span: 'md:col-span-4 lg:col-span-4 row-span-2', accent: '#059669', popupImage: '/feature-commissions.webp' },
  { icon: Zap, title: 'Quick Notes & Templates', desc: 'TipTap-powered notes with highlighting, templates, and instant search. Your deal context, organized.', span: 'md:col-span-4 lg:col-span-4', accent: '#7c3aed', popupImage: '/feature-notes.webp' },
  { icon: TrendingUp, title: 'Task Management', desc: 'Color-coded to-do lists with rich text checklists. Completed tasks auto-sort to the bottom.', span: 'md:col-span-4 lg:col-span-4', accent: '#d97706', popupImage: '/feature-todos.webp' },
  { icon: MessageCircle, title: 'AI Deal Assistant', desc: 'Ask your deal anything. The AI reads every note and detail — then gives you instant, actionable answers you can paste straight into email.', span: 'md:col-span-4 lg:col-span-6', accent: '#7c3aed', popupImage: '/feature-assistant.webp' },
  { icon: Activity, title: 'Pipeline Pulse', desc: 'A weekly check-in on the deals that need your attention. Flags stale accounts, stalled proposals, upcoming renewals, and re-engagement opportunities — automatically.', span: 'md:col-span-4 lg:col-span-6', accent: '#e11d48', popupImage: '/feature-pulse.webp' },
];

const moreFeatures = [
  { icon: Bell, title: 'Smart Reminders', desc: 'Custom deal reminders that surface at exactly the right time — never let a follow-up slip.' },
  { icon: ClipboardList, title: 'Manager Deal Forms', desc: 'Build custom form templates, assign them to reps on a weekly cadence, and export all answers to Excel.' },
  { icon: Users, title: 'Team Management', desc: 'Managers can track rep pipelines, view team analytics, comment on deals, and invite members with a link.' },
  { icon: CheckSquare, title: 'Task Management', desc: 'Color-coded to-do lists with rich text checklists. Completed tasks auto-sort to the bottom.' },
  { icon: Download, title: 'Export & Import', desc: 'Bulk upload accounts via Excel. One-click data exports for reporting and analysis.' },
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Floating Nav */}
      <nav className="fixed top-5 left-1/2 -translate-x-1/2 z-50 rounded-full backdrop-blur-xl bg-slate-900/90 border border-slate-700/50 flex justify-between items-center px-6 md:px-8 py-3 w-[92%] max-w-5xl shadow-2xl" data-testid="landing-nav">
        <h1 className="text-lg md:text-xl font-black tracking-tighter text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          deal <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">desk</span>
        </h1>
        <div className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-slate-300 hover:text-white transition-colors text-sm font-medium">Features</a>
          <a href="#more" className="text-slate-300 hover:text-white transition-colors text-sm font-medium">Why Deal Desk</a>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="login-nav-link" onClick={() => navigate('/login')}
            className="text-slate-300 hover:text-white transition-colors text-sm font-medium hidden sm:block">
            Sign In
          </button>
          <button data-testid="signup-nav-link" onClick={() => navigate('/register')}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full px-5 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5">
            Get Started Free
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="flex flex-col lg:flex-row min-h-screen" data-testid="hero-section">
        {/* Left Dark Panel */}
        <div className="w-full lg:w-1/2 bg-[#1e1b4b] text-white flex flex-col justify-center p-8 md:p-12 lg:p-20 min-h-[60vh] lg:min-h-screen relative overflow-hidden">
          {/* Subtle background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/10 rounded-full blur-3xl" />

          <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10">
            <motion.p variants={fadeUp} className="text-indigo-400 uppercase tracking-[0.2em] text-xs md:text-sm font-bold mb-6">
              Made by Sales, for Sales
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your deals.<br />
              <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">Your pipeline.</span><br />
              Your way.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-300 text-base md:text-lg leading-relaxed max-w-md mb-8">
              The lightweight CRM built for account executives who want to close more, track commissions, and never miss a follow-up.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4">
              <button data-testid="hero-cta-button" onClick={() => navigate('/register')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full px-8 py-4 font-bold text-lg transition-all transform hover:-translate-y-1 shadow-[0_0_20px_rgba(79,70,229,0.4)]">
                Start 30-day free trial <ArrowRight size={20} className="inline ml-2" />
              </button>
              <button onClick={() => navigate('/login')}
                className="text-slate-400 hover:text-white transition-colors font-medium text-sm flex items-center gap-1">
                Already have an account? <ChevronRight size={16} />
              </button>
            </motion.div>
            <motion.p variants={fadeUp} className="text-xs text-slate-500 mt-3">
              Then $4/month. No credit card required. Cancel anytime.
            </motion.p>
          </motion.div>
        </div>

        {/* Right Light Panel */}
        <div className="w-full lg:w-1/2 bg-[#f8fafc] flex items-center justify-center p-6 md:p-10 lg:p-12 min-h-[40vh] lg:min-h-screen relative overflow-hidden">
          <div className="relative w-full">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <img
                src={`/hero-screenshot.webp${IMG_V}`}
                alt="Deal Desk Dashboard"
                width="2880"
                height="1800"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                className="w-full max-w-[720px] lg:max-w-[900px] xl:max-w-[1100px] 2xl:max-w-[1280px] object-contain rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.18)] border border-slate-200/50"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-8 -right-2 md:-right-4"
            >
              <img
                src={`/commissions-screenshot.webp${IMG_V}`}
                alt="Commission Tracker"
                width="1440"
                height="900"
                loading="lazy"
                decoding="async"
                className="w-48 md:w-64 lg:w-80 xl:w-[360px] 2xl:w-[420px] object-contain rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.22)] border-2 border-white"
                data-testid="hero-image-secondary"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-20 lg:py-32 px-4 md:px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-indigo-600 uppercase tracking-[0.2em] text-xs font-bold mb-4">Everything you need</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built for how you actually sell
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
            {features.map((f, idx) => {
              const Icon = f.icon;
              const isHovered = hoveredFeature === idx;
              return (
                <motion.div key={idx} variants={fadeUp}
                  onMouseEnter={() => f.popupImage && setHoveredFeature(idx)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  className={`${f.span} bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-500 group relative overflow-hidden ${f.popupImage ? 'cursor-pointer' : ''}`}
                  data-testid={`feature-card-${idx}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-500" style={{ backgroundColor: f.accent }} />
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: f.accent + '15' }}>
                    <Icon size={24} style={{ color: f.accent }} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-slate-500 leading-relaxed">{f.desc}</p>
                  {f.popupImage && (
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                          className="overflow-hidden"
                        >
                          <img
                            src={`${f.popupImage}${IMG_V}`}
                            alt={f.title}
                            className="w-full rounded-xl shadow-lg border border-slate-200"
                            data-testid={`feature-preview-${idx}`}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Scrollable Highlights */}
      <section id="more" className="py-20 lg:py-32 bg-[#f8fafc]" data-testid="more-features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Sticky Left */}
            <div className="lg:w-2/5 lg:sticky lg:top-32 lg:self-start">
              <p className="text-indigo-600 uppercase tracking-[0.2em] text-xs font-bold mb-4">And there's more</p>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Everything you need to close
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">From smart reminders to team collaboration, Deal Desk has the tools your sales org needs — without the bloat of enterprise CRMs.</p>
              <button data-testid="more-cta-button" onClick={() => navigate('/register')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-semibold transition-all hover:-translate-y-0.5">
                Get Started Free <ArrowRight size={16} className="inline ml-1" />
              </button>
            </div>

            {/* Scrolling Right */}
            <div className="lg:w-3/5 space-y-6">
              {moreFeatures.map((f, idx) => {
                const Icon = f.icon;
                return (
                  <motion.div key={idx}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                    className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors">
                        <Icon size={20} className="text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-28 px-6" data-testid="pricing-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3">Pricing</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            One plan. No surprises.
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="text-slate-500 text-lg max-w-xl mx-auto mb-12">
            Try every feature free for 30 days. No credit card. Cancel anytime.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
            className="bg-white border-2 border-indigo-600 rounded-3xl shadow-[0_20px_60px_-15px_rgba(79,70,229,0.25)] overflow-hidden text-left"
            data-testid="landing-pricing-card">
            <div className="px-8 md:px-12 py-10 md:py-12">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} className="text-indigo-600" />
                    <h3 className="text-2xl font-bold text-slate-900">Deal Desk Pro</h3>
                  </div>
                  <p className="text-sm text-slate-500">Everything you need to run a clean pipeline.</p>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-6xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>$4</span>
                  <span className="text-base text-slate-500 font-medium">/month</span>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-8 flex items-start gap-2">
                <Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-emerald-900 font-semibold">
                  Start with a 30-day free trial — no credit card required.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-10">
                {[
                  'Unlimited deals & accounts',
                  'AI Deal Assistant',
                  { label: 'Google Calendar sync', soon: true },
                  { label: 'Salesforce sync', soon: true },
                  'Rich-text deal feed & notes',
                  'Reminders & to-do lists',
                  'Commission calculator',
                  'Pipeline Pulse audit',
                  'Bulk import & export',
                  'Manager team views',
                  'Email support',
                ].map((item, i) => {
                  const isObj = typeof item === 'object';
                  const label = isObj ? item.label : item;
                  const soon = isObj && item.soon;
                  return (
                    <div key={label} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`${soon ? 'text-slate-300' : 'text-emerald-600'} mt-0.5 flex-shrink-0`} />
                      <span className={soon ? 'text-slate-500' : 'text-slate-700'}>
                        {label}
                        {soon && (
                          <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Coming Soon</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                data-testid="pricing-cta-button"
                onClick={() => navigate('/register')}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl py-4 font-bold text-base transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.5)]"
              >
                Start your 30-day free trial
              </button>
              <p className="text-center text-xs text-slate-400 mt-3">
                No card up front. Cancel from your dashboard at any time.
              </p>
            </div>
          </motion.div>

          <p className="mt-8 text-xs text-slate-400">
            Billed monthly via <span className="font-semibold">Stripe</span>. Prices in USD. Subscription includes any future Pro feature we ship.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 px-4 lg:px-8">
        <div className="bg-[#1e1b4b] rounded-[3rem] text-center py-20 md:py-32 px-8 relative overflow-hidden" data-testid="cta-section">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-indigo-600/15 rounded-full blur-3xl" />
          <div className="relative z-10">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to close more deals?
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="text-slate-400 text-lg mb-8 max-w-md mx-auto">
              Join sales teams who manage their pipeline with Deal Desk.
            </motion.p>
            <motion.button initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              data-testid="footer-cta-button"
              onClick={() => navigate('/register')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full px-8 py-4 font-bold text-lg transition-all transform hover:-translate-y-1 shadow-[0_0_30px_rgba(79,70,229,0.4)]">
              Start 30-day free trial
            </motion.button>
            <p className="text-slate-500 text-xs mt-3">Then $4/month. No card required.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 text-center">
        <p className="text-sm text-slate-400">
          <span className="font-bold text-slate-500" style={{ fontFamily: 'Outfit, sans-serif' }}>deal desk</span> &middot; Made by Sales, for Sales
        </p>
        <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-400">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-slate-700 transition-colors">Privacy Policy</Link>
          <span className="text-slate-300">·</span>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-slate-700 transition-colors">Terms of Service</Link>
          <span className="text-slate-300">·</span>
          <span>&copy; 2026 2a Productions, Inc.</span>
        </div>
      </footer>
    </div>
  );
};
